import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import type { SeriesDayVm } from "@/features/daily/components/executive/series-view-model";
import { dailyFormat } from "@/features/daily/components/command/command-view-model";

type Props = {
    days: SeriesDayVm[];
    /** The colour of the current series: the page's green for revenue, blue otherwise. */
    tone?: "blue" | "green";
    /** ISO currency of the values, for the y-axis; `null` falls back to a plain number. */
    currency?: string | null;
    label: string;
};

type Palette = {
    blue: string;
    green: string;
    grid: string;
    text: string;
    fontSize: number;
};

/**
 * Canvas needs literal colours, so the chart reads the page's own `--daily-*` tokens
 * off its container instead of carrying a palette of its own — a TV breakpoint that
 * scales the type scales the axis labels too.
 */
const readPalette = (node: HTMLElement): Palette => {
    const style = getComputedStyle(node);
    const token = (name: string): string => style.getPropertyValue(name).trim();
    const fontSize = Number.parseFloat(token("--daily-text-xs"));
    return {
        blue: token("--daily-blue"),
        green: token("--daily-green"),
        grid: token("--daily-line"),
        text: token("--daily-muted"),
        fontSize: Number.isNaN(fontSize) ? 11 : fontSize,
    };
};

/**
 * D110 + B1: one measure per day, the elapsed days only — no projection and no future
 * day drawn at zero. The previous period is dashed and stops where it has no
 * equivalent day.
 * Everything drawn here is also in the table next to it (WCAG 1.1.1).
 */
export const SeriesChart: React.FC<Props> = ({ days, label, tone = "blue", currency }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [palette, setPalette] = useState<Palette | null>(null);

    useEffect(() => {
        const node = containerRef.current;
        if (node === null) return undefined;
        const update = (): void => setPalette(readPalette(node));
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);

    const option = useMemo(() => {
        if (palette === null) return null;
        const main = tone === "green" ? palette.green : palette.blue;
        const money = (value: number): string => dailyFormat.compactMoney(value, currency ?? null);
        const axisLabel = { color: palette.text, fontSize: palette.fontSize };
        return {
            animation: false,
            grid: { left: 8, right: 8, top: 12, bottom: 8, containLabel: true },
            tooltip: {
                trigger: "axis",
                // D136: the current day's total with what it is made of, then the previous period.
                formatter: (params: unknown): string => {
                    const first = Array.isArray(params) ? params[0] : params;
                    const index = typeof first === "object" && first !== null && "dataIndex" in first && typeof first.dataIndex === "number" ? first.dataIndex : -1;
                    const day = days[index];
                    if (day === undefined) return "";
                    // D139: the split only when both types carry an amount that day.
                    const split = day.currentByType.order !== 0 && day.currentByType.deposit !== 0
                        ? [`· Órdenes: ${money(day.currentByType.order)}`, `· Depósitos: ${money(day.currentByType.deposit)}`]
                        : [];
                    return [
                        `<strong>${day.label}</strong>`,
                        `Actual: ${money(day.current)}`,
                        ...split,
                        `Anterior: ${day.reference === null ? "—" : money(day.reference)}`,
                    ].join("<br/>");
                },
            },
            xAxis: { type: "category", data: days.map((day) => day.label), axisLabel, axisLine: { lineStyle: { color: palette.grid } }, axisTick: { show: false } },
            yAxis: {
                type: "value",
                axisLabel: { ...axisLabel, formatter: (value: number) => money(value) },
                splitLine: { lineStyle: { color: palette.grid, type: "dashed" } },
            },
            series: [
                {
                    name: "Periodo anterior",
                    type: "line",
                    data: days.map((day) => day.reference),
                    smooth: true,
                    showSymbol: false,
                    connectNulls: false,
                    lineStyle: { color: main, width: 2, type: "dashed", opacity: 0.55 },
                    itemStyle: { color: main },
                },
                {
                    name: "Actual",
                    type: "line",
                    data: days.map((day) => day.current),
                    smooth: true,
                    showSymbol: false,
                    lineStyle: { color: main, width: 3 },
                    itemStyle: { color: main },
                    areaStyle: { color: main, opacity: 0.12 },
                },
            ],
        };
    }, [days, palette, tone, currency]);

    return (
        <div ref={containerRef} className="daily-chart" role="img" aria-label={label}>
            {option === null ? null : (
                // D132: absolutely filling its box, the canvas follows the card whatever the layout
                // (a flexed box has no definite height for a percentage), and resizes with it. The
                // explicit height overrides echarts-for-react's own default of 300 px.
                <ReactECharts
                    option={option}
                    notMerge
                    autoResize
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                />
            )}
        </div>
    );
};
